/**
 * What a mission waiting on a person reads on every backlog view (tree, table, board), from one
 * resolver: "Needs you" while it waits on an answer or a pending approval (what it asks is the
 * tooltip), and "Blocked: approval expired" (or denied, refused by policy) when a decided request
 * stopped a session (R-HL-1, R-HL-3). Icon plus label; nothing otherwise.
 */
import { Hand, ShieldAlert, ShieldQuestion, ShieldX } from '@orgus/ui/icons';
import { APPROVAL_BLOCK_LABEL } from '@orgus/protocol';
import type { Blocker, MissionView } from '@orgus/protocol';
import { Badge, Tooltip, cn } from '@orgus/ui';
import { StatePill } from './parts.tsx';

export interface ChipModel {
  kind: 'blocked' | 'policy' | 'pending';
  label: string;
  /** The blocker's full sentence, for the tooltip. */
  title: string;
}

/** Pure: which chip a mission's blockers call for. A blocked approval wins over a pending one. */
export function chipOf(blockers: readonly Blocker[]): ChipModel | null {
  const blocked = blockers.find((b) => b.kind === 'approval' && b.outcome && b.phase !== 'resuming');
  if (blocked) {
    const policy = blocked.outcome === 'denied' && blocked.asked?.by === 'policy';
    const label =
      blocked.phase === 'resume_failed'
        ? APPROVAL_BLOCK_LABEL.resume_failed
        : blocked.phase === 'not_run'
          ? APPROVAL_BLOCK_LABEL.not_run
          : blocked.outcome === 'expired'
            ? APPROVAL_BLOCK_LABEL.expired
            : policy
              ? APPROVAL_BLOCK_LABEL.policy
              : APPROVAL_BLOCK_LABEL.denied;
    return { kind: policy ? 'policy' : 'blocked', label, title: blocked.title };
  }
  const pending = blockers.find((b) => b.kind === 'approval' && !b.outcome);
  if (pending) return { kind: 'pending', label: 'Approval needed', title: pending.title };
  return null;
}

export function MissionBlockerChip({ view }: { view: Pick<MissionView, 'blockers'> }) {
  const c = chipOf(view.blockers);
  if (!c) return null;
  const icon = c.kind === 'pending' ? <ShieldQuestion /> : c.kind === 'policy' ? <ShieldX /> : <ShieldAlert />;
  return (
    <Tooltip content={c.title}>
      <span tabIndex={0} className="inline-flex max-w-full min-w-0">
        <Badge tone={c.kind === 'pending' ? 'waiting' : 'failed'} icon={icon} className="min-w-0 [&>span]:truncate">
          <span className="truncate">{c.label}</span>
        </Badge>
      </span>
    </Tooltip>
  );
}

export interface AttentionModel {
  /** needs-you: it waits on an answer or a pending approval. blocked: a decided request stopped it. */
  kind: 'needs-you' | 'blocked';
  /** "Needs you", or the block: "Blocked: approval expired". */
  label: string;
  /** What it asks, or why it is blocked, in full: the tooltip. */
  detail: string | null;
}

/** Pure: whether a mission waits on a person, and how every view names it. */
export function attentionOf(view: Pick<MissionView, 'state' | 'blockers'>): AttentionModel | null {
  const c = chipOf(view.blockers);
  if (c && c.kind !== 'pending') return { kind: 'blocked', label: c.label, detail: c.title };
  if (view.state !== 'waiting' && !c) return null;
  const ask = view.blockers.find((b) => (b.kind === 'approval' && !b.outcome) || b.kind === 'waiting' || b.kind === 'idle');
  return { kind: 'needs-you', label: 'Needs you', detail: ask?.title ?? null };
}

/**
 * The one attention chip of a backlog row or card: "Needs you" (amber) or the block (red). `short`
 * says only "Blocked" where a status column is narrow; the full sentence is always the tooltip.
 */
export function AttentionChip({ view, short, className }: { view: Pick<MissionView, 'state' | 'blockers'>; short?: boolean; className?: string }) {
  const a = attentionOf(view);
  if (!a) return null;
  const pill = a.kind === 'needs-you' ? <StatePill tone="attention" icon={Hand} label="Needs you" variant="soft" className="min-w-0" /> : <StatePill tone="danger" icon={ShieldAlert} label={short ? 'Blocked' : a.label} variant="soft" className="min-w-0" />;
  if (!a.detail) return <span className={cn('inline-flex min-w-0', className)}>{pill}</span>;
  return (
    <Tooltip content={a.detail}>
      <span tabIndex={0} className={cn('inline-flex min-w-0 rounded-pill', className)} aria-label={`${a.kind === 'needs-you' ? 'Needs you' : a.label}: ${a.detail}`}>
        {pill}
      </span>
    </Tooltip>
  );
}
